import {Show} from 'platform/foundation';
import {cva} from 'platform/shared';

import {ChangeEvent, useId} from 'react';

import {suffixTestId, TestIdProps, useBoolean} from 'shared/web';

import {FormControlProps} from '../../../types/FormControlProps';
import {callAllHandlers} from '../../../utils/callAllHandlers';
import {dataAttr} from '../../../utils/dataAttr';

export interface RadioItemProps extends FormControlProps<boolean>, TestIdProps {
  children?: string;
  name?: string;
  // A unique ID for the input element, allowing it to be associated with a <label> element in another component.
  externalLabelId?: string;
}

const radioControlVariants = cva(
  'h-5 w-5 shrink-0 rounded-full border border-solid border-[var(--color-palettes-neutral-200-100)] bg-[var(--color-palettes-white-10-100)]',
  {
    variants: {
      isChecked: {
        true: 'border-4 border-[var(--color-accent)]',
        false: '',
      },
      isDisabled: {
        true: 'cursor-not-allowed bg-transparent opacity-40',
        false: '',
      },
    },
  }
);

export function RadioItem(props: RadioItemProps) {
  const id = useId();
  const [isFocused, handleFocus, handleBlur] = useBoolean();
  const [isHovered, handleHover, handleLeave] = useBoolean();

  const isInvalid = props.isInvalid || !!props.errorMessage;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (props.isDisabled) {
      event.preventDefault();
      return;
    }

    props.onChange?.(props.value);
  };

  const rootProps = {
    'data-disabled': dataAttr(props.isDisabled),
    'data-checked': dataAttr(props.value),
    'data-invalid': dataAttr(isInvalid),
  };

  const checkboxProps = {
    'data-hover': dataAttr(isHovered),
    'data-checked': dataAttr(props.value),
    'data-disabled': dataAttr(props.isDisabled),
    'data-invalid': dataAttr(isInvalid),
    'data-focus': dataAttr(isFocused),
  };

  return (
    <label
      htmlFor={props.externalLabelId ?? id}
      title={props.children}
      {...rootProps}
      data-testid={suffixTestId('radioWrapper', props)}
      className="relative flex cursor-pointer select-none items-center"
    >
      <input
        id={props.externalLabelId ?? id}
        type="radio"
        name={props.name}
        onChange={handleChange}
        onFocus={callAllHandlers(props.onFocus, handleFocus)}
        onBlur={callAllHandlers(props.onBlur, handleBlur)}
        disabled={props.isDisabled}
        checked={props.value}
        required={props.isRequired}
        data-testid={suffixTestId('radioInput', props)}
        className="sr-only"
      />
      <span
        onMouseEnter={handleHover}
        onMouseLeave={handleLeave}
        {...checkboxProps}
        data-testid={suffixTestId('radioControl', props)}
        className={radioControlVariants({
          isChecked: props.value,
          isDisabled: Boolean(props.isDisabled),
        })}
      />
      <Show when={props.children}>
        <span
          data-testid={suffixTestId('radioLabel', props)}
          className="ml-3 text-[length:var(--text-size-small)] font-[var(--text-weight-default)] leading-[var(--text-leading-small)]"
        >
          {props.children}
        </span>
      </Show>
    </label>
  );
}
